import { useState, useEffect, useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useInfiniteTimeline, chunkAuthors, assembleTimelinePage } from '@/hooks/useInfiniteTimeline';
import { Button } from '@/components/ui/button';
import { FeedList } from '@/desktop/components/FeedList';
import { ThreadView } from '@/desktop/components/ThreadView';
import { TrendingTags } from '@/desktop/components/TrendingTags';
import { buildSearch, DITTO_RELAY, type DittoSort } from '@/lib/ditto';
import { cn } from '@/lib/utils';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';
import { RefreshCw, Users, Globe, Flame } from 'lucide-react';
import { useDesktop } from '@/desktop/DesktopContext';

type FeedTab = 'following' | 'global' | 'discover';

function FeedSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="px-3 sm:px-4 py-3 border-b border-border/40">
          <div className="flex gap-2.5">
            <Skeleton className="w-9 h-9 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3 w-14" />
              </div>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Following feed page shape returned by the single, self-contained query.
 * Contacts are fetched inside the queryFn so the query key never flip-flops
 * between "no contacts loaded yet" and "contacts loaded" states.
 *
 * The feed itself is fetched as one REQ per ≤100-author chunk, fired in
 * parallel: relays silently stall on filters with hundreds of authors (they
 * never EOSE and the query returns nothing), which was the root cause of the
 * perpetually empty Following tab for users with large follow lists.
 */
interface FollowingPage {
  events: NostrEvent[];
  followCount: number;
  nextUntil: number | null;
}

function FollowingFeed({
  onOpenThread,
  onOpenProfile,
}: {
  onOpenThread: (event: NostrEvent) => void;
  onOpenProfile: (pubkey: string) => void;
}) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  const {
    data,
    isLoading,
    refetch,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['feed', 'following', user?.pubkey],
    queryFn: async ({ pageParam, signal }): Promise<FollowingPage> => {
      if (!user) return { events: [], followCount: 0, nextUntil: null };

      // 1. Fetch the contact list (kind 3) first — a small single-author
      // filter, which relays answer quickly.
      const contactEvents = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );
      const followedPubkeys = contactEvents.length > 0
        ? contactEvents[0].tags.filter(([t]) => t === 'p').map(([, pk]) => pk).filter(Boolean)
        : [];

      const authorPubkeys = Array.from(new Set([user.pubkey, ...followedPubkeys]));
      if (authorPubkeys.length === 0) {
        return { events: [], followCount: 0, nextUntil: null };
      }

      // 2. Chunk the authors (≤100 per REQ) and spread the page size across
      // chunks, keeping a floor so quiet chunks still contribute.
      const chunks = chunkAuthors(authorPubkeys);
      const perChunkLimit = chunks.length > 1
        ? Math.max(15, Math.ceil(50 / chunks.length))
        : 50;
      const base: NostrFilter = { kinds: [1] };
      if (pageParam) base.until = pageParam;

      // 3. Fire all chunk queries in parallel — each is its own subscription.
      // A failing chunk degrades to empty instead of killing the whole page.
      const results = await Promise.all(
        chunks.map((c) =>
          nostr.query([{ ...base, authors: c, limit: perChunkLimit }], {
            signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]),
          }).catch(() => [] as NostrEvent[]),
        ),
      );

      // 4. Deduplicate.
      const seen = new Set<string>();
      const fetched: NostrEvent[] = [];
      for (const events of results) {
        for (const event of events) {
          if (!seen.has(event.id)) { seen.add(event.id); fetched.push(event); }
        }
      }

      // 5. Assemble the page.
      const page = assembleTimelinePage(fetched, chunks, perChunkLimit);
      return { events: page.events, followCount: followedPubkeys.length, nextUntil: page.nextUntil };
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextUntil ?? undefined,
    enabled: !!user,
    staleTime: 30000,
  });

  // Flatten pages.
  const events = useMemo(() => {
    const seen = new Set<string>();
    const flat: NostrEvent[] = [];
    for (const page of data?.pages ?? []) {
      for (const event of page.events) {
        if (!seen.has(event.id)) { seen.add(event.id); flat.push(event); }
      }
    }
    return flat;
  }, [data]);

  const followCount = data?.pages[0]?.followCount ?? 0;

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground px-6 text-center">
        <Users className="w-10 h-10 opacity-30" />
        <p className="text-sm">Log in to see posts from people you follow.</p>
      </div>
    );
  }

  if (isLoading) {
    return <FeedSkeleton />;
  }

  if (followCount === 0 && events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground px-6 text-center">
        <Users className="w-10 h-10 opacity-30" />
        <p className="text-sm">You're not following anyone yet. Switch to Global to discover people.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/40 bg-muted/10">
        <span className="text-[10px] text-muted-foreground">{followCount} following</span>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <FeedList
        events={events}
        onOpenThread={onOpenThread}
        onOpenProfile={onOpenProfile}
        emptyMessage="No posts found from your follows. Try refreshing or checking relay connections."
        hasNextPage={!!hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </div>
  );
}

function GlobalFeed({
  onOpenThread,
  onOpenProfile,
}: {
  onOpenThread: (event: NostrEvent) => void;
  onOpenProfile: (pubkey: string) => void;
}) {
  const { events, isLoading, refetch, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteTimeline({
    queryKey: ['feed', 'global'],
    filter: { kinds: [1] },
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/40 bg-muted/10">
        <span className="text-[10px] text-muted-foreground">Latest from relays</span>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {isLoading ? (
        <div className="flex-1 overflow-auto min-h-0"><FeedSkeleton /></div>
      ) : (
        <FeedList
          events={events}
          onOpenThread={onOpenThread}
          onOpenProfile={onOpenProfile}
          emptyMessage="No notes found. Check your relay connections."
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      )}
    </div>
  );
}

const DISCOVER_SORTS: { id: DittoSort; label: string }[] = [
  { id: 'hot', label: 'Hot' },
  { id: 'top', label: 'Top' },
  { id: 'rising', label: 'Rising' },
  { id: 'new', label: 'New' },
];

function DiscoverFeed({
  onOpenThread,
  onOpenProfile,
}: {
  onOpenThread: (event: NostrEvent) => void;
  onOpenProfile: (pubkey: string) => void;
}) {
  const { nostr } = useNostr();
  const [sort, setSort] = useState<DittoSort>('hot');
  const [tag, setTag] = useState<string | null>(null);

  // Ranked results can't paginate with a time cursor (it would break rank
  // order), so each page re-requests a larger limit; the last page is always
  // the full, freshest superset.
  const { data, isLoading, refetch, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['feed', 'discover', sort, tag ?? ''],
    queryFn: async ({ pageParam, signal }) => {
      // Ditto's relay returns results already ranked — preserve that order.
      return nostr.query(
        [{ kinds: [1], search: buildSearch(tag ? `#${tag}` : '', { sort }), limit: pageParam }],
        { relays: [DITTO_RELAY], signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]) },
      );
    },
    initialPageParam: 40,
    getNextPageParam: (lastPage, _pages, lastParam) =>
      lastPage.length >= lastParam && lastParam < 200 ? lastParam + 40 : undefined,
    staleTime: 30000,
  });
  const events = data?.pages[data.pages.length - 1];

  return (
    <div className="flex flex-col h-full">
      {/* Trending hashtag chips */}
      <div className="px-3 py-1.5 border-b border-border/40 bg-muted/10">
        <TrendingTags onSelect={(t) => setTag(t)} />
      </div>
      {/* Sort selector + active filter + refresh */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-border/40 bg-muted/5">
        {tag ? (
          <button
            onClick={() => setTag(null)}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[11px] font-medium"
          >
            #{tag} <span className="opacity-70">×</span>
          </button>
        ) : (
          DISCOVER_SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
                sort === s.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))
        )}
        <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {isLoading ? (
        <div className="flex-1 overflow-auto min-h-0"><FeedSkeleton /></div>
      ) : (
        <FeedList
          events={events ?? []}
          onOpenThread={onOpenThread}
          onOpenProfile={onOpenProfile}
          emptyMessage="Nothing trending here right now. Try another sort."
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      )}
    </div>
  );
}

export function FeedApp() {
  const [tab, setTab] = useState<FeedTab>('following');
  const [threadEvent, setThreadEvent] = useState<NostrEvent | null>(null);
  const { pendingThread, clearPendingThread, openProfile } = useDesktop();

  // Tabs mount on first visit and stay mounted afterwards (hidden), so
  // switching back is instant and preserves each feed's scroll position,
  // loaded pages, and sort/filter state.
  const [visited, setVisited] = useState<ReadonlySet<FeedTab>>(() => new Set<FeedTab>(['following']));
  const switchTab = (t: FeedTab) => {
    setTab(t);
    setVisited((prev) => (prev.has(t) ? prev : new Set(prev).add(t)));
  };

  // Handle incoming thread requests from other apps (e.g. Notifications)
  useEffect(() => {
    if (pendingThread && !threadEvent) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setThreadEvent(pendingThread);
      clearPendingThread();
    }
  }, [pendingThread, threadEvent, clearPendingThread]);

  if (threadEvent) {
    return <ThreadView event={threadEvent} onBack={() => setThreadEvent(null)} />;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex items-center gap-0 px-3 py-2 border-b border-border/50 bg-muted/30 shrink-0">
        <button
          onClick={() => switchTab('following')}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            tab === 'following'
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="w-3 h-3" />
          Following
        </button>
        <button
          onClick={() => switchTab('discover')}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            tab === 'discover'
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Flame className="w-3 h-3" />
          Discover
        </button>
        <button
          onClick={() => switchTab('global')}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            tab === 'global'
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Globe className="w-3 h-3" />
          Global
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {visited.has('following') && (
          <div className={cn('h-full', tab !== 'following' && 'hidden')}>
            <FollowingFeed onOpenThread={setThreadEvent} onOpenProfile={openProfile} />
          </div>
        )}
        {visited.has('discover') && (
          <div className={cn('h-full', tab !== 'discover' && 'hidden')}>
            <DiscoverFeed onOpenThread={setThreadEvent} onOpenProfile={openProfile} />
          </div>
        )}
        {visited.has('global') && (
          <div className={cn('h-full', tab !== 'global' && 'hidden')}>
            <GlobalFeed onOpenThread={setThreadEvent} onOpenProfile={openProfile} />
          </div>
        )}
      </div>
    </div>
  );
}
